<script setup lang="ts">
import * as z from "zod";
// import { Icon } from '@iconify/vue'

definePageMeta({
  layout: false,
});

/**
 * ✅ Zod schema
 */
const schema = z.object({
  email: z.string().email("Invalid email address"),
  // Login validates an EXISTING credential — Entra is the authority on the
  // real policy, so only require non-empty here (a 16-char client minimum would
  // lock out any user whose password predates that policy).
  password: z.string().min(1, "Enter your password"),
});

type Schema = z.output<typeof schema>;

/**
 * ✅ Form state used by UForm + UInput v-model
 */
const state = reactive<Partial<Schema>>({
  email: "",
  password: "",
});

const toast = useToast();
const route = useRoute();
const returnTo = (route.query.returnTo as string) || "/";
// "Add another account" (multi-account Switch user): the visitor is already
// signed in as someone else and is adding a second account to this device. Just
// adjusts the copy — the sign-in itself joins the device set server-side.
const isAdd = computed(() => route.query.add === "1");

const cfg = useRuntimeConfig();
// This page can only authenticate when the custom native-auth LOGIN is ON
// (its /api/auth/basic-login endpoint 404s otherwise). If it's off, fall back
// to the Microsoft-hosted OIDC login so a stray visit to /login still works.
onMounted(() => {
  if (!cfg.public.nativeAuthLoginEnabled) {
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
  }
});

/**
 * ✅ UI state
 */
const loading = ref(false);
// Show/hide password toggle (eye icon).
const showPassword = ref(false);
// "Keep me signed in on this device" — opts this device into the 90-day
// trusted-session window so the email OTP isn't needed again here for a while.
// Sent to /api/auth/basic-login and carried across the MFA step server-side.
const trustDevice = ref(false);

/**
 * ✅ serverError (wrong credentials) - separate from zod validation
 */
const serverError = ref<string | null>(null);
const authFailed = ref(false); // ✅ NEW: server says credentials are wrong
/**
 * ✅ Control when validation UI is shown
 * - attemptedSubmit: show validation after pressing submit once
 * - touched: show validation after blur
 */
const attemptedSubmit = ref(false);
const touched = reactive({
  email: false,
  password: false,
});

/**
 * ✅ Validation results
 */
const validation = computed(() => schema.safeParse(state));

const zodFieldErrors = computed(() => {
  if (validation.value.success) return {};
  return validation.value.error.flatten().fieldErrors;
});

const loginFormIsValid = computed(() => validation.value.success);

/**
 * ✅ helpers for UI rules
 */
const emailHasValue = computed(() => !!state.email?.trim());
const passwordHasValue = computed(() => !!state.password);

const showEmailUI = computed(
  () => emailHasValue.value && (touched.email || attemptedSubmit.value),
);

const showPasswordUI = computed(
  () => passwordHasValue.value && (touched.password || attemptedSubmit.value),
);

/**
 * ✅ IMPORTANT:
 * field invalid/valid is based ONLY on zod
 * serverError should NOT force fields invalid (prevents cached red)
 */

const emailInvalid = computed(() => {
  if (!showEmailUI.value) return false;
  return !!zodFieldErrors.value.email?.length || serverInvalid.value;
});

const passwordInvalid = computed(() => {
  if (!showPasswordUI.value) return false;
  return !!zodFieldErrors.value.password?.length || serverInvalid.value;
});

const emailValid = computed(() => {
  // Green does NOT wait for blur/submit: a password-manager fill drops a valid
  // value into a field the user never focuses, so a touch-gated green left
  // autofilled rows looking untouched. Red stays gated (see emailInvalid) so
  // nobody is scolded mid-typing.
  if (!emailHasValue.value) return false;
  if (serverInvalid.value) return false;
  return !zodFieldErrors.value.email?.length;
});

const passwordValid = computed(() => {
  // Same as emailValid: eager green so autofilled credentials read as accepted.
  if (!passwordHasValue.value) return false;
  if (serverInvalid.value) return false;
  return !zodFieldErrors.value.password?.length;
});

/**
 * ✅ Inline error message:
 * 1) show serverError after submit attempt
 * 2) else show zod field errors
 */
const inlineErrorMessage = computed(() => {
  if (attemptedSubmit.value && serverError.value) return serverError.value;

  if (!(attemptedSubmit.value || touched.email || touched.password)) return "";

  return (
    (showEmailUI.value ? zodFieldErrors.value.email?.[0] : "") ||
    (showPasswordUI.value ? zodFieldErrors.value.password?.[0] : "") ||
    ""
  );
});

const showInvalidMessage = computed(() => !!inlineErrorMessage.value);

/**
 * ✅ KEY FEATURE:
 * Clear server error "cache" immediately when user edits email or password
 */

watch(
  () => [state.email, state.password],
  () => {
    if (authFailed.value) authFailed.value = false;
    if (serverError.value) serverError.value = null;
  },
);

const serverInvalid = computed(() => {
  // only after submit + server responded with auth failure
  return attemptedSubmit.value && authFailed.value;
});

/**
 * ✅ OPTIONAL: if both fields cleared -> reset UI to fresh state
 */
watch(
  () => [state.email, state.password],
  ([email, pass]) => {
    const emptyEmail = !email?.trim();
    const emptyPass = !pass;

    if (emptyEmail && emptyPass) {
      attemptedSubmit.value = false;
      touched.email = false;
      touched.password = false;
      serverError.value = null;
    }
  },
);

/**
 * ✅ Submit handler
 */
// const onSubmit = async () => {
//   attemptedSubmit.value = true
//   serverError.value = null
//
//   // ✅ Don't call server if invalid
//   if (!validation.value.success) return
//
//   loading.value = true
//
//   try {
//     const reqFetch = useRequestFetch()
//
//     const res: any = await reqFetch('/api/auth/basic-login', {
//       method: 'POST',
//       headers: { 'content-type': 'application/json' },
//       body: {
//         username: state.email,
//         password: state.password,
//         returnTo
//       }
//     })
//
//     if (res?.ok) {
//       toast.add({
//         title: 'Welcome',
//         description: 'Logged-in successfully.',
//         color: 'success'
//       })
//
//       return navigateTo((res.redirectTo && res.redirectTo !== '/') || '/household/overview')
//     }
//
//     throw new Error('Login failed')
//   } catch (e) {
//     // ✅ Wrong credentials -> server error message
//     serverError.value = 'Enter a valid email and password'
//   } finally {
//     loading.value = false
//   }
// }
const onSubmit = async () => {
  attemptedSubmit.value = true;
  serverError.value = null;
  authFailed.value = false;

  if (!validation.value.success) return;

  loading.value = true;
  try {
    const reqFetch = useRequestFetch();

    const res: any = await reqFetch("/api/auth/basic-login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: {
        username: state.email,
        password: state.password,
        returnTo,
        trustDevice: trustDevice.value,
      },
    });

    if (res?.ok) {
      toast.add({
        title: "Welcome",
        description: "Logged-in successfully.",
        color: "success",
      });
      // A fresh sign-in must never inherit a prior tab's "View as" preview
      // (e.g. re-authing into an admin via the switcher's /login?add=1 step-up).
      // A stale preview would downgrade hasDisplayRole and 403 the admin routes.
      try {
        sessionStorage.removeItem("view-as");
      } catch {
        /* ignore */
      }
      // HARD navigation (full reload): the session cookie was just set, but
      // useAuth() still holds the pre-login `authenticated:false` cached by the
      // global middleware. A soft navigateTo gets bounced by that stale state;
      // a full document load re-inits auth with the new session. (Same reason as
      // otp.vue. This is the no-MFA path; the MFA path hands off to /otp below.)
      return navigateTo(res.redirectTo || "/household/overview", {
        external: true,
      });
    }
    if (res?.useBrowserFlow) {
      // Entra answered the native token request with challenge_type=redirect:
      // this account can't complete in-app sign-in (typically no MFA factor
      // enrolled yet — native auth can use a factor but cannot enrol one). Hand
      // off to the OIDC browser flow, which can enrol and then signs them in.
      // Previously this surfaced as "Enter a valid email and password", i.e. a
      // correct password reported as wrong.
      // prompt=login + login_hint are BOTH required. Without prompt, Entra
      // silently reuses whatever session the browser already has and signs you
      // in as THAT account — so entering a client's credentials landed you back
      // in the admin account, and "add account" kept re-adding the admin.
      // login_hint aims the forced re-auth at the identity actually typed.
      const params = new URLSearchParams({
        prompt: "login",
        login_hint: state.email,
        returnTo: returnTo || "/",
      });
      return navigateTo("/api/auth/login?" + params.toString(), {
        external: true,
      });
    }
    if (res?.mfaRequired) {
      // Tenant requires a second factor — hand off to the OTP step. `channel`
      // (email|sms) drives the OTP page copy; `masked` is the masked target;
      // `channels` lists every method the user can switch to.
      return navigateTo({
        path: "/otp",
        query: {
          masked: res.maskedTarget || "",
          channel: res.channel || "email",
          channels: (res.availableChannels || []).join(","),
          len: res.codeLength || 6,
          returnTo,
        },
      });
    }

    throw new Error("Login failed");
  } catch (e) {
    authFailed.value = true; // ✅ IMPORTANT
    serverError.value = "Enter a valid email and password";
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div
    class="md:bg-elevated/25 md:bg-gradient-to-tl md:from-[#193322]/100 from-20% to-[#0A5024]/100 w-full min-h-screen flex flex-col items-center justify-start md:justify-center py-8"
  >
    <!-- Auth diff AU2: the card gains elevation; it was a flat 0-radius,
         no-shadow panel. The review suggested ~6-8px of radius, but 2px is
         the chosen house value for this build (same call as the profile
         form fields) — don't "restore" 8px from the review note.
         BOTH are md:-scoped on purpose — below md the gradient backdrop is
         off (see the wrapper's `md:bg-gradient-to-tl`), so the card fills a
         white page and a radius + shadow there would draw a floating edge
         against nothing. -->
    <div
      class="flex flex-col items-center bg-white md:h-fit text-center px-23 py-16 max-[639px]:px-6 max-[639px]:py-12 md:rounded-[2px] md:shadow-[0_16px_40px_rgba(9,32,20,0.28)]"
    >
      <div class="flex flex-col justify-center items-center">
        <img
          alt="Azzad Logo"
          src="~/assets/img/Azzad Asset Logo Symbol Square.png"
          class="p-3 w-18 border-2 border-gray-200 rounded mb-4"
        />

        <h1
          class="text-[1.75rem] pt-4 font-semibold font-playfair text-black tracking-wide"
        >
          {{ isAdd ? "Add another account" : "Welcome to Azzad" }}
        </h1>

        <h1
          class="text-[1.75rem] font-semibold font-playfair text-black tracking-wide"
        >
          {{ isAdd ? "Sign in to switch to it" : "Log into your account" }}
        </h1>

        <p
          v-if="isAdd"
          class="text-sm pt-4 font-light font-nunito text-black max-w-100 mx-auto"
        >
          You'll be able to switch between this and your other accounts without
          signing in each time.
        </p>
        <p v-else class="text-sm pt-4 font-light font-nunito text-black">
          Don't have an account?
          <a
            href="https://azzadasset.com/contact/"
            rel="noopener noreferrer"
            target="_blank"
            class="text-sm font-normal font-nunito text-[#284E36] underline"
          >
            Contact us
          </a>
        </p>
      </div>

      <div class="mt-13 w-full">
        <UForm
          :schema="schema"
          :state="state"
          class="w-100 max-[500px]:w-full mx-auto"
          @submit.prevent="onSubmit"
        >
          <!-- ✅ Group wrapper -->
          <!-- AU4 border: the group wrapper was drawn in #DBE0F1, a second
               near-identical grey that only these two auth pages used. It
               now matches the portal's input border (#C9D3DD). Radius stays
               at the house 2px. -->
          <div
            class="w-full border border-[#C9D3DD] rounded-[2px] overflow-hidden"
          >
            <!-- Email row -->
            <div
              class="relative p-4 border"
              :class="
                emailInvalid
                  ? 'bg-[#FEF7F8] border-red-700'
                  : emailValid
                    ? 'bg-[#F3FBF5] border-[#2E7D32]'
                    : 'bg-white border-transparent'
              "
            >
              <p class="text-[0.625rem] font-light text-[#455668] text-start">
                Email Address
              </p>

              <span
                v-if="emailInvalid"
                class="absolute right-11 top-[calc(50%+6px)] -translate-y-1/2"
              >
                <UIcon
                  name="lucide:alert-circle"
                  class="w-6 h-6 text-red-700"
                />
              </span>

              <UInput
                v-model="state.email"
                type="email"
                variant="none"
                required
                class="w-full pr-16 text-[#455668]"
                placeholder="Enter email address"
                :ui="{
                  base: 'w-full !ring-0 text-base !shadow-none !px-0 !py-1',
                }"
                @blur="touched.email = true"
              />
            </div>

            <!-- Divider -->
            <div
              class="h-px"
              :class="
                emailInvalid
                  ? 'bg-red-700'
                  : emailValid
                    ? 'bg-[#2E7D32]'
                    : 'bg-[#C9D3DD]'
              "
            />

            <!-- Password row -->
            <!--              border-top: aliceblue; -->

            <div
              class="relative p-4 border"
              :class="
                passwordInvalid
                  ? 'bg-[#FEF7F8] border-red-700 border-t border-t-[#F0F8FF]'
                  : passwordValid
                    ? 'bg-[#F3FBF5] border-[#2E7D32] border-t border-t-[#F0F8FF]'
                    : 'bg-white border-transparent'
              "
            >
              <p class="text-[0.625rem] font-light text-[#455668] text-start">
                Password
              </p>
              <span
                v-if="passwordInvalid"
                class="absolute right-11 top-[calc(50%+6px)] -translate-y-1/2"
              >
                <UIcon
                  name="lucide:alert-circle"
                  class="w-6 h-6 text-red-700"
                />
              </span>

              <!-- Show/hide password toggle -->
              <button
                type="button"
                tabindex="-1"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword"
                class="absolute right-3 top-[calc(50%+6px)] -translate-y-1/2 z-10 inline-flex items-center justify-center text-[#9AA7B5] hover:text-[#455668] transition-colors cursor-pointer"
                @click="showPassword = !showPassword"
              >
                <UIcon
                  :name="showPassword ? 'lucide:eye-off' : 'lucide:eye'"
                  class="w-5 h-5"
                />
              </button>

              <UInput
                v-model="state.password"
                :type="showPassword ? 'text' : 'password'"
                variant="none"
                required
                class="w-full pr-16 text-[#455668]"
                placeholder="Enter password"
                :ui="{
                  base:
                    'w-full !ring-0 !shadow-none !px-0 !py-1 leading-none ' +
                    (state.password && !showPassword
                      ? 'text-[1.1875rem] tracking-[0.35em]'
                      : 'text-base tracking-normal'),
                }"
                @blur="touched.password = true"
              />
            </div>
          </div>

          <!-- Error line + forgot password -->
          <div class="flex mt-2 justify-between">
            <p
              class="text-[#CD0A1E] justify-start text-sm font-normal font-nunito leading-5 max-[639px]:pr-3"
            >
              {{ showInvalidMessage ? inlineErrorMessage : "" }}
            </p>

            <a
              href="/forgot-password"
              class="text-xs pb-4 font-thin text-green-500 underline"
            >
              Forgot password?
            </a>
          </div>

          <!-- Trust this device: opt into the 90-day session so we don't ask
               for a verification code on this device again for a while. -->
          <label
            class="flex items-center gap-2 mb-1 cursor-pointer select-none"
          >
            <input
              v-model="trustDevice"
              type="checkbox"
              class="size-4 shrink-0 rounded-xs border-[#C9D3DD] text-[#284E36] accent-[#284E36] cursor-pointer"
            />
            <span class="text-sm font-nunito text-[#455668]"
              >Keep me signed in on this device</span
            >
          </label>

          <UButton
            type="submit"
            class="cursor-pointer w-full h-15 justify-center rounded-[2px] mt-4 uppercase p-3 font-black tracking-wider bg-[#C79900] text-white disabled:bg-gray-300 disabled:text-white/70 disabled:opacity-100"
            :disabled="loading || !loginFormIsValid"
          >
            <span
              v-if="!loading"
              class="justify-start text-Color-Generic-White text-base font-black font-nunito uppercase leading-5 tracking-wide"
            >
              Sign in
            </span>

            <span v-else> Signing in… </span>
          </UButton>
        </UForm>
      </div>
    </div>

    <div
      class="flex text-sm flex-col items-center justify-between mt-10 px-4 w-fit"
    >
      <p class="mx-auto text-center">
        <a class="text-[#648972] font-light">By signing, you agree to the</a>
        <a
          href="https://azzadasset.com/wp-content/uploads/2023/10/Azzad-Terms-and-Conditions.pdf"
          class="text-[#56A975] underline"
        >
          Terms of Service
        </a>
        <a class="text-[#648972] font-light">
          and to occasionally receive emails from us.
        </a>
      </p>

      <p class="mx-auto text-center mt-3">
        <a class="text-[#648972] font-light"> Please read our </a>
        <a
          href="https://azzadasset.com/privacy-policy/"
          class="text-[#56A975] underline"
        >
          Privacy Policy
        </a>
        <a class="text-[#648972] font-light"> to learn more.</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Chrome/Safari paint their own autofill background over the input, which hid
   the row's valid-green (and error-red) tint behind a blue-grey block. There is
   no way to turn that background off, so delay it past the heat death of the
   session and restate the text colour the fill also overrides. */
:deep(input:-webkit-autofill),
:deep(input:-webkit-autofill:hover),
:deep(input:-webkit-autofill:focus),
:deep(input:-webkit-autofill:active) {
  -webkit-text-fill-color: #455668;
  caret-color: #455668;
  transition:
    background-color 600000s 0s,
    color 600000s 0s;
}
</style>
